import { useEffect, useState } from "react";
import { useNavigate, Link, useLocation } from "react-router-dom";
import { ArrowRight, Plus, Trash2, Save, Repeat } from "lucide-react";
import {
  academyContentAPI,
  academyQuestionsAPI,
  academySkillsAPI,
  getApiError,
} from "../../services/academyService";
import { ACADEMY_ROOT, DIFFICULTY_OPTIONS } from "./academyConstants";
import QuestionTextEditor from "./components/QuestionTextEditor";
import MediaDropField from "./components/MediaDropField";
import { findContentItem } from "./utils/findContentItem";
import TranslationField from "../../components/TranslationField";

const LABELS = {
  vocabulary: "سؤال مفردات",
  grammar: "سؤال قواعد",
  "reading-question": "سؤال قراءة",
  "listening-question": "سؤال استماع",
  "speaking-question": "سؤال تحدث",
};
const CHILD_KINDS = ["reading-question", "listening-question", "speaking-question"];

// ─── تحويل نوع السؤال (مهارات MIXED فقط) ───
const KIND_TO_TYPE = {
  vocabulary: "VOCABULARY",
  grammar: "GRAMMAR",
  "reading-question": "READING",
  "listening-question": "LISTENING",
  "speaking-question": "SPEAKING",
};
const TYPE_LABELS = {
  VOCABULARY: "مفردات",
  GRAMMAR: "قواعد",
  READING: "قراءة",
  LISTENING: "استماع",
  SPEAKING: "تحدث",
};
const CONVERT_TYPES = Object.keys(TYPE_LABELS);
const PARENT_TYPES = ["READING", "LISTENING", "SPEAKING"];
const PARENT_NOUN = { READING: "القطعة", LISTENING: "التسجيل", SPEAKING: "الفيديو" };
const EMPTY_PARENT = {
  title: "",
  passage_text: "",
  translation: "",
  source: "",
  transcript: "",
  description: "",
  duration: "",
};

const inputCls =
  "w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500";

// الـ rich text editor بيرجّع "<p></p>" لما يكون فاضي، فنحوّله لنص فاضي قبل الإرسال
const isRichEmpty = (html) => !html || html.replace(/<[^>]*>/g, "").trim() === "";
const cleanRich = (html) => (isRichEmpty(html) ? "" : html);

// كل خيار ليه key ثابت عشان الـ editors ما تخلطش المحتوى لما نحذف خيار
let optUid = 0;
const makeOptions = (texts) => texts.map((text) => ({ key: ++optUid, text }));

export default function AcademyMCQForm({ kind, skillId, itemId, parentId }) {
  const navigate = useNavigate();
  const location = useLocation();
  const isEdit = Boolean(itemId);
  const isChild = CHILD_KINDS.includes(kind);
  const backPath = `${ACADEMY_ROOT}/skills/${skillId}`;
  const currentType = KIND_TO_TYPE[kind];

  const [loading, setLoading] = useState(false);
  const [initLoading, setInitLoading] = useState(isEdit);
  const [errors, setErrors] = useState({});
  const [form, setForm] = useState({
    question_text: "",
    translation: "",
    explanation: "",
    english_explanation: "",
    points: 1,
    difficulty: "MEDIUM",
    is_active: true,
  });
  const [options, setOptions] = useState(() => makeOptions(["", "", "", ""]));
  const [correctIndex, setCorrectIndex] = useState(null);
  const [imageFile, setImageFile] = useState(null);
  const [existingImageUrl, setExistingImageUrl] = useState(null);
  const [imageCleared, setImageCleared] = useState(false);

  // ─── حالة التحويل ───
  const [skillType, setSkillType] = useState(null);
  const [allEntries, setAllEntries] = useState([]); // عشان قايمة الآباء الموجودين
  const [targetType, setTargetType] = useState(currentType);
  const [parentMode, setParentMode] = useState("existing"); // existing | new
  const [targetParentId, setTargetParentId] = useState("");
  const [newParent, setNewParent] = useState(EMPTY_PARENT);
  const [newParentFile, setNewParentFile] = useState(null);

  const canConvert = isEdit && skillType === "MIXED";
  const isConverting = canConvert && targetType !== currentType;
  const availableParents = PARENT_TYPES.includes(targetType)
    ? allEntries.filter((e) => e.type === targetType)
    : [];

  // لما النوع المستهدف يتغير نصفّر بيانات الأب
  useEffect(() => {
    setParentMode("existing");
    setTargetParentId("");
    setNewParent(EMPTY_PARENT);
    setNewParentFile(null);
  }, [targetType]);

  useEffect(() => {
    if (!isEdit) return;
    const passed = location.state?.item;
    if (passed) {
      applyItem(passed);
      setInitLoading(false);
    }
    (async () => {
      try {
        // الباك إند بيقص page_size عند 100
        const [skill, data] = await Promise.all([
          academySkillsAPI.getById(skillId),
          academyQuestionsAPI.getSkillQuestions(skillId, 1, 100),
        ]);
        setSkillType(skill.skill_type);
        const entries = data.questions || [];
        setAllEntries(entries);
        if (!passed) {
          const found = findContentItem(entries, kind, itemId);
          if (found) applyItem(found);
          else setErrors({ general: "تعذر تحميل بيانات السؤال" });
        }
      } catch (err) {
        if (!passed) setErrors({ general: getApiError(err, "تعذر تحميل بيانات السؤال") });
      } finally {
        if (!passed) setInitLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEdit, itemId]);

  const applyItem = (item) => {
    setForm({
      question_text: item.question_text || "",
      translation: item.translation || "",
      explanation: item.explanation || "",
      english_explanation: item.english_explanation || "",
      points: item.points ?? 1,
      difficulty: item.difficulty || "MEDIUM",
      is_active: item.is_active ?? true,
    });
    const raw = [item.choice_a, item.choice_b, item.choice_c, item.choice_d].filter((o) => o !== "");
    const padded = raw.length ? [...raw, ...Array(4 - raw.length).fill("")] : ["", "", "", ""];
    setOptions(makeOptions(padded));
    const idx = ["A", "B", "C", "D"].indexOf(item.correct_answer);
    setCorrectIndex(idx >= 0 ? idx : null);
    setExistingImageUrl(item.question_image || null);
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((prev) => ({ ...prev, [name]: type === "checkbox" ? checked : value }));
  };
  const handleOptionChange = (idx, val) =>
    setOptions((prev) => prev.map((o, i) => (i === idx ? { ...o, text: val } : o)));
  const addOption = () => options.length < 4 && setOptions((prev) => [...prev, ...makeOptions([""])]);
  const removeOption = (idx) => {
    if (options.length <= 2) return;
    setOptions((prev) => prev.filter((_, i) => i !== idx));
    if (correctIndex === idx) setCorrectIndex(null);
    else if (correctIndex > idx) setCorrectIndex((c) => c - 1);
  };
  const setNP = (name, value) => setNewParent((p) => ({ ...p, [name]: value }));

  // بيرجّع رسالة خطأ لو بيانات الأب ناقصة، أو null
  const validateParent = () => {
    if (!isConverting || !PARENT_TYPES.includes(targetType)) return null;
    if (parentMode === "existing") {
      return targetParentId ? null : `اختار ${PARENT_NOUN[targetType]} موجودة، أو اختار "إنشاء جديد"`;
    }
    if (!newParent.title.trim()) return "العنوان مطلوب";
    if (targetType === "READING" && !newParent.passage_text.replace(/<[^>]*>/g, "").trim())
      return "نص القطعة مطلوب";
    if (targetType !== "READING" && !newParentFile)
      return targetType === "LISTENING" ? "الملف الصوتي مطلوب" : "ملف الفيديو مطلوب";
    return null;
  };

  const appendParentData = (fd) => {
    fd.append("target_type", targetType);
    if (!PARENT_TYPES.includes(targetType)) return;
    if (parentMode === "existing") {
      fd.append("target_parent_id", targetParentId);
      return;
    }
    fd.append("new_parent_title", newParent.title);
    fd.append("new_parent_difficulty", form.difficulty);
    if (targetType === "READING") {
      fd.append("new_parent_passage_text", newParent.passage_text);
      fd.append("new_parent_translation", newParent.translation || "");
      fd.append("new_parent_source", newParent.source);
    } else if (targetType === "LISTENING") {
      fd.append("new_parent_transcript", cleanRich(newParent.transcript));
      if (newParent.duration !== "") fd.append("new_parent_duration", newParent.duration);
      fd.append("new_parent_audio_file", newParentFile);
    } else {
      fd.append("new_parent_description", cleanRich(newParent.description));
      if (newParent.duration !== "") fd.append("new_parent_duration", newParent.duration);
      fd.append("new_parent_video_file", newParentFile);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const isEmpty = !form.question_text || form.question_text.replace(/<[^>]*>/g, "").trim() === "";
    if (isEmpty) return setErrors({ question_text: "نص السؤال مطلوب" });

    const filled = options.map((o) => o.text).filter((t) => !isRichEmpty(t));
    if (correctIndex === null) return setErrors({ correct_answer: "لازم تختار الإجابة الصحيحة" });
    if (isRichEmpty(options[correctIndex].text))
      return setErrors({ correct_answer: "الإجابة الصحيحة لازم يكون ليها نص" });
    if (filled.length < 2) return setErrors({ options: "لازم تضيف اختيارين على الأقل" });

    const parentError = validateParent();
    if (parentError) return setErrors({ parent: parentError });

    setLoading(true);
    setErrors({});
    try {
      const fd = new FormData();
      fd.append("question_text", form.question_text);
      fd.append("translation", form.translation || "");
      fd.append("explanation", cleanRich(form.explanation));
      fd.append("english_explanation", cleanRich(form.english_explanation));
      fd.append("points", form.points);
      fd.append("difficulty", form.difficulty);
      fd.append("is_active", form.is_active ? "True" : "False");
      fd.append("correct_answer", options[correctIndex].text);
      fd.append("options", JSON.stringify(filled));

      if (!isEdit) {
        if (isChild) fd.append("parent_id", parentId);
        else fd.append("academy_skill", skillId);
      }
      if (imageFile) fd.append("question_image", imageFile);
      else if (isEdit && imageCleared) fd.append("question_image", "");

      if (isConverting) {
        appendParentData(fd);
        await academyContentAPI.convert(kind, itemId, fd);
      } else if (isEdit) {
        await academyContentAPI.update(kind, itemId, fd);
      } else {
        await academyContentAPI.create(kind, fd);
      }
      navigate(backPath);
    } catch (err) {
      const data = err.response?.data || {};
      const fieldKeys = ["question_text", "options", "correct_answer"];
      const hasFieldError = fieldKeys.some((k) => data[k]);
      setErrors(hasFieldError ? data : { ...data, general: getApiError(err) });
    } finally {
      setLoading(false);
    }
  };

  if (initLoading) return <div className="max-w-xl mx-auto py-16 text-center text-gray-400">جارِ التحميل...</div>;

  return (
    <div className="max-w-xl mx-auto space-y-6">
      <div className="flex items-center gap-3">
        <Link to={backPath} className="p-2 rounded-lg bg-gray-100 hover:bg-gray-200 text-gray-700 transition-colors">
          <ArrowRight className="w-5 h-5" />
        </Link>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{isEdit ? "تعديل" : "إضافة"} {LABELS[kind] || "سؤال"}</h1>
          <p className="text-gray-500 text-sm">اختيار من متعدد</p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-5 bg-white rounded-2xl border border-gray-200 p-6">
        {/* ─── تغيير نوع السؤال (Mixed + تعديل فقط) ─── */}
        {canConvert && (
          <div className="border border-indigo-200 bg-indigo-50 rounded-xl p-3 space-y-3">
            <div>
              <label className="flex items-center gap-1.5 text-xs font-semibold text-indigo-800 mb-2">
                <Repeat className="w-3.5 h-3.5" />
                نوع السؤال
              </label>
              <div className="flex gap-2 flex-wrap">
                {CONVERT_TYPES.map((t) => (
                  <button
                    key={t}
                    type="button"
                    onClick={() => setTargetType(t)}
                    className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors ${
                      targetType === t
                        ? "bg-indigo-600 text-white"
                        : "bg-white text-indigo-600 border border-indigo-200 hover:bg-indigo-100"
                    }`}
                  >
                    {TYPE_LABELS[t]}
                  </button>
                ))}
              </div>
              {isConverting && (
                <p className="text-xs text-indigo-700 mt-2 leading-relaxed">
                  هيتحوّل السؤال من {TYPE_LABELS[currentType]} إلى {TYPE_LABELS[targetType]}.
                  محاولات الطلاب ونقاطهم هتتنقل للسؤال الجديد.
                </p>
              )}
            </div>

            {isConverting && PARENT_TYPES.includes(targetType) && (
              <div className="border border-gray-200 rounded-xl p-3 space-y-3 bg-white">
                <div className="flex gap-2">
                  {[
                    ["existing", "استخدام موجود"],
                    ["new", "إنشاء جديد"],
                  ].map(([mode, label]) => (
                    <button
                      key={mode}
                      type="button"
                      onClick={() => setParentMode(mode)}
                      className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors ${
                        parentMode === mode
                          ? "bg-emerald-600 text-white"
                          : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                      }`}
                    >
                      {label}
                    </button>
                  ))}
                </div>

                {parentMode === "existing" ? (
                  <div>
                    <label className="text-xs font-medium text-gray-600 mb-1 block">
                      اختر {PARENT_NOUN[targetType]}
                    </label>
                    <select value={targetParentId} onChange={(e) => setTargetParentId(e.target.value)} className={inputCls}>
                      <option value="">-- اختر --</option>
                      {availableParents.map((p) => (
                        <option key={p.id} value={p.id}>{p.title}</option>
                      ))}
                    </select>
                    {availableParents.length === 0 && (
                      <p className="text-xs text-gray-400 mt-1">لا يوجد عناصر من هذا النوع بعد — اختر "إنشاء جديد"</p>
                    )}
                  </div>
                ) : (
                  <div className="space-y-3">
                    <input
                      value={newParent.title}
                      onChange={(e) => setNP("title", e.target.value)}
                      placeholder="العنوان"
                      className={inputCls}
                    />
                    {targetType === "READING" && (
                      <>
                        <QuestionTextEditor
                          value={newParent.passage_text}
                          onChange={(html) => setNP("passage_text", html)}
                        />
                        <TranslationField
                          label="ترجمة نص القطعة (اختياري)"
                          value={newParent.translation}
                          onChange={(v) => setNP("translation", v)}
                        />
                        <input
                          value={newParent.source}
                          onChange={(e) => setNP("source", e.target.value)}
                          placeholder="المصدر (اختياري)"
                          className={inputCls}
                        />
                      </>
                    )}
                    {targetType === "LISTENING" && (
                      <>
                        <MediaDropField
                          label="الملف الصوتي" kind="audio" accept="audio/*" required
                          file={newParentFile} onChange={setNewParentFile}
                        />
                        <QuestionTextEditor
                          value={newParent.transcript}
                          onChange={(html) => setNP("transcript", html)}
                          placeholder="النص المكتوب (اختياري)"
                        />
                      </>
                    )}
                    {targetType === "SPEAKING" && (
                      <>
                        <MediaDropField
                          label="ملف الفيديو" kind="video" accept="video/*" required
                          file={newParentFile} onChange={setNewParentFile}
                        />
                        <QuestionTextEditor
                          value={newParent.description}
                          onChange={(html) => setNP("description", html)}
                          placeholder="الوصف (اختياري)"
                        />
                      </>
                    )}
                    {targetType !== "READING" && (
                      <input
                        type="number" min={0}
                        value={newParent.duration}
                        onChange={(e) => setNP("duration", e.target.value)}
                        placeholder="المدة بالثواني (اختياري)"
                        className={inputCls}
                      />
                    )}
                  </div>
                )}
              </div>
            )}
            {errors.parent && <p className="text-red-500 text-xs">{errors.parent}</p>}
          </div>
        )}

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">نص السؤال <span className="text-red-500">*</span></label>
          <QuestionTextEditor value={form.question_text} onChange={(html) => setForm((p) => ({ ...p, question_text: html }))} />
          {errors.question_text && <p className="text-red-500 text-xs mt-1">{errors.question_text}</p>}
        </div>

        <TranslationField
          label="ترجمة نص السؤال (اختياري)"
          value={form.translation}
          onChange={(v) => setForm((p) => ({ ...p, translation: v }))}
        />

        <MediaDropField
          label="صورة السؤال (اختياري)" kind="image" accept="image/*"
          file={imageFile} existingUrl={existingImageUrl} cleared={imageCleared}
          onChange={setImageFile} onClear={() => setImageCleared(true)}
        />

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            الاختيارات <span className="text-red-500">*</span>{" "}
            <span className="text-xs text-gray-400">(اختر الإجابة الصحيحة)</span>
          </label>
          <div className="space-y-3">
            {options.map((opt, idx) => (
              <div key={opt.key} className="flex items-start gap-2">
                <button
                  type="button"
                  onClick={() => setCorrectIndex(idx)}
                  className={`w-6 h-6 mt-2 rounded-full border-2 shrink-0 transition-colors ${
                    correctIndex === idx ? "bg-green-500 border-green-500" : "border-gray-300 hover:border-green-400"
                  }`}
                />
                <div className="flex-1 min-w-0">
                  <QuestionTextEditor
                    value={opt.text}
                    onChange={(html) => handleOptionChange(idx, html)}
                    placeholder={`اختيار ${String.fromCharCode(65 + idx)}`}
                  />
                </div>
                <button
                  type="button"
                  onClick={() => removeOption(idx)}
                  className="text-red-400 hover:text-red-600 p-1 mt-2"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
          {options.length < 4 && (
            <button type="button" onClick={addOption} className="mt-2 text-sm text-indigo-600 hover:text-indigo-800 flex items-center gap-1">
              <Plus className="w-3 h-3" /> إضافة اختيار
            </button>
          )}
          {errors.options && <p className="text-red-500 text-xs mt-1">{errors.options}</p>}
          {errors.correct_answer && <p className="text-red-500 text-xs mt-1">{errors.correct_answer}</p>}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">الشرح (اختياري)</label>
          <QuestionTextEditor
            value={form.explanation}
            onChange={(html) => setForm((p) => ({ ...p, explanation: html }))}
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">الشرح بالإنجليزي (اختياري)</label>
          <QuestionTextEditor
            value={form.english_explanation}
            onChange={(html) => setForm((p) => ({ ...p, english_explanation: html }))}
          />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">النقاط</label>
            <input type="number" name="points" value={form.points} onChange={handleChange} min={1}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">مستوى الصعوبة</label>
            <select name="difficulty" value={form.difficulty} onChange={handleChange}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500">
              {DIFFICULTY_OPTIONS.map((d) => <option key={d.value} value={d.value}>{d.label}</option>)}
            </select>
          </div>
        </div>

        <label className="flex items-center gap-2 text-sm text-gray-700">
          <input type="checkbox" name="is_active" checked={form.is_active} onChange={handleChange}
            className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500" />
          نشط
        </label>

        {errors.general && <p className="text-red-500 text-sm bg-red-50 p-3 rounded-lg">{errors.general}</p>}

        <button type="submit" disabled={loading}
          className="w-full flex items-center justify-center gap-2 bg-indigo-600 hover:bg-indigo-700 disabled:bg-indigo-400 text-white py-3 rounded-lg font-medium transition-colors">
          <Save className="w-5 h-5" />
          {loading ? "جارِ الحفظ..." : isConverting ? "تحويل وحفظ" : "حفظ السؤال"}
        </button>
      </form>
    </div>
  );
}